import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  Button,
  Alert,
} from "@mui/material";

import { FaEdit, FaTrash, FaKey } from "react-icons/fa";
import { ContentCopy } from "@mui/icons-material";

import AddEmployeeDialog from "./AddEmployeeDialog";
import EditEmployeeDialog from "./EditEmployeeDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import axiosInstance from "../../axiosInstance";
import { getLoginUserRoleId } from "../../_lib";
import Stack from "@mui/material/Stack";

function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openReset, setOpenReset] = useState(false);
  const [tempPassword, setTempPassword] = useState(""); 

  const loginUserRoleId = getLoginUserRoleId();

  // --------------------------------------------
  // Load employees from backend
  // --------------------------------------------
  const fetchEmployees = async () => {
    try {
      const res = await axiosInstance.get("/employees");
      setEmployees(res.data);
    } catch (err) {
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // --------------------------------------------
  // Add employee to list (after backend creation)
  // --------------------------------------------
  const handleAddEmployee = (newEmp) => {
    fetchEmployees();
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    let result = "";
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setTempPassword(result);
  };

  const handleResetClick = (emp) => {
    setSelectedEmployee(emp);
    generatePassword();
    setOpenReset(true);
  };

  const handleConfirmReset = async () => {
    try {
      await axiosInstance.post("/auth/update-password", {
        userId: selectedEmployee?._id,
        newPassword: tempPassword,
        reset:true,
      });
      setOpenReset(false);
      toast.success("Password successfully reset");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to update password"
      );
    }
  };

  // --------------------------------------------
  // Update employee in list (after edit)
  // --------------------------------------------
  const handleUpdateEmployee = (updatedEmp) => {
    fetchEmployees();
  };

  // --------------------------------------------
  // Delete employee from backend + list
  // --------------------------------------------
  const handleDeleteEmployee = async () => {
    try {
      await axiosInstance.delete(`/employees/${selectedEmployee.userId}`);

      setEmployees(
        employees.filter((e) => e.userId !== selectedEmployee.userId)
      );

      setOpenDelete(false);
    } catch (err) {
      setError("Failed to delete employee");
    }
  };

  // --------------------------------------------
  // Loading spinner
  // --------------------------------------------
  const getRowStyles = (roleName) => {
    const name = (roleName || '').toLowerCase();
    let leftBorderColor = '#64748b'; // default slate
    let bgColor = 'rgba(100, 116, 139, 0.005)';
    let borderColor = 'rgba(100, 116, 139, 0.08)';
    let hoverBg = 'rgba(100, 116, 139, 0.015) !important';
    let hoverBorderColor = 'rgba(100, 116, 139, 0.2)';

    if (name.includes('admin')) {
      leftBorderColor = 'var(--primary-color, #4f46e5)';
      bgColor = 'var(--primary-light, rgba(99, 102, 241, 0.01))';
      borderColor = 'var(--primary-light, rgba(99, 102, 241, 0.06))';
      hoverBg = 'var(--primary-light, rgba(99, 102, 241, 0.025)) !important';
      hoverBorderColor = 'var(--primary-color, rgba(99, 102, 241, 0.18))';
    } else if (name.includes('manager')) {
      leftBorderColor = '#d97706';
      bgColor = 'rgba(245, 158, 11, 0.01)';
      borderColor = 'rgba(245, 158, 11, 0.06)';
      hoverBg = 'rgba(245, 158, 11, 0.025) !important';
      hoverBorderColor = 'rgba(245, 158, 11, 0.18)';
    } else if (name.includes('staff') || name.includes('employee')) {
      leftBorderColor = '#10b981';
      bgColor = 'rgba(16, 185, 129, 0.01)';
      borderColor = 'rgba(16, 185, 129, 0.06)';
      hoverBg = 'rgba(16, 185, 129, 0.025) !important';
      hoverBorderColor = 'rgba(16, 185, 129, 0.18)';
    }

    return {
      backgroundColor: bgColor,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
      '& td': {
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: borderColor,
      },
      '& td:first-of-type': {
        borderLeft: '1px solid',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderColor: borderColor,
        borderLeftColor: `${leftBorderColor} !important`,
        borderLeftWidth: '4px !important',
      },
      '& td:last-of-type': {
        borderRight: '1px solid',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        borderColor: borderColor,
      },
      "&:hover": {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        backgroundColor: hoverBg,
        '& td': {
          borderColor: hoverBorderColor,
        }
      }
    };
  };

  const renderRoleBadge = (roleName) => {
    let bg = 'rgba(100, 116, 139, 0.08)';
    let border = 'rgba(100, 116, 139, 0.15)';
    let color = '#475569';

    const name = (roleName || '').toLowerCase();
    if (name.includes('admin')) {
      bg = 'var(--primary-light, rgba(99, 102, 241, 0.08))';
      border = 'rgba(99, 102, 241, 0.15)';
      color = 'var(--primary-color, #4f46e5)';
    } else if (name.includes('manager')) {
      bg = 'rgba(245, 158, 11, 0.08)';
      border = 'rgba(245, 158, 11, 0.15)';
      color = '#d97706';
    } else if (name.includes('staff') || name.includes('employee')) {
      bg = 'rgba(16, 185, 129, 0.08)';
      border = 'rgba(16, 185, 129, 0.15)';
      color = '#059669';
    }

    return (
      <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.25,
        borderRadius: '12px',
        backgroundColor: bg,
        border: '1px solid',
        borderColor: border,
        color: color,
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'capitalize'
      }}>
        {roleName}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Employees
        </Typography>

        {/* Add Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button 
            variant="contained" 
            onClick={() => setOpenAdd(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '20px',
              fontWeight: 600,
              px: 3,
              py: 1,
              color: 'var(--primary-contrast, #ffffff) !important',
              background: 'var(--primary-gradient, linear-gradient(135deg, #4f46e5 0%, #6366f1 100%))',
              boxShadow: '0 4px 12px var(--primary-shadow, rgba(99, 102, 241, 0.2))',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                filter: 'brightness(0.92) saturate(1.06)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 22px var(--primary-shadow, rgba(99, 102, 241, 0.35))',
              },
              '&:active': {
                transform: 'translateY(0)',
                filter: 'brightness(0.85)',
              }
            }}
          >
            Add Employee
          </Button>
        </Box>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Dialogs */}
      <AddEmployeeDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={handleAddEmployee}
      />

      <EditEmployeeDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        employee={selectedEmployee}
        onUpdate={handleUpdateEmployee}
      />

      <ConfirmDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDeleteEmployee}
        message={`Are you sure you want to delete ${selectedEmployee?.fullName}?`}
      />

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 'none', backgroundColor: 'transparent' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ '& th': { border: 'none', backgroundColor: 'transparent', color: 'text.secondary', py: 1 } }}>
                <TableCell>
                  <b>User ID</b>
                </TableCell>
                <TableCell>
                  <b>Name</b>
                </TableCell>
                <TableCell>
                  <b>Mobile</b>
                </TableCell>
                {loginUserRoleId !== 2 && loginUserRoleId !== 3 && (
                  <TableCell>
                    <b>Employer</b>
                  </TableCell>
                )}
                <TableCell>
                  <b>Role</b>
                </TableCell>
                <TableCell>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {employees.map((emp) => {
                const rowStyle = getRowStyles(emp.roleName);
                return (
                  <TableRow 
                    key={emp.userId}
                    sx={rowStyle}
                  >
                    <TableCell sx={{ py: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                      {emp.userId}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      {emp.fullName}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {emp.mobile}
                    </TableCell>
                    {loginUserRoleId !== 2 && loginUserRoleId !== 3 && (
                      <TableCell sx={{ py: 1.5 }}>
                        {emp.employerDetails?.employerName || '—'}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 1.5 }}>
                      {renderRoleBadge(emp.roleName)}
                    </TableCell>

                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          sx={{
                            color: 'var(--primary-color, #4f46e5)',
                            backgroundColor: 'var(--primary-light, rgba(99, 102, 241, 0.05))',
                            '&:hover': {
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setOpenEdit(true);
                          }}
                        >
                          <FaEdit size={15} />
                        </IconButton>

                        {loginUserRoleId === 1 && (
                          <IconButton
                            size="small"
                            sx={{
                              color: '#d97706',
                              backgroundColor: 'rgba(245, 158, 11, 0.05)',
                              '&:hover': {
                                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                transform: 'scale(1.05)'
                              },
                              transition: 'all 0.2s',
                            }}
                            onClick={() => handleResetClick(emp)}
                          >
                            <FaKey size={13} />
                          </IconButton>
                        )}

                        <IconButton
                          size="small"
                          sx={{
                            color: '#ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.15)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setOpenDelete(true);
                          }}
                        >
                          <FaTrash size={13} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No employees found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
      <ConfirmDialog
        open={openReset}
        onClose={() => setOpenReset(false)}
        onConfirm={handleConfirmReset}
        title="Reset Employee Password"
        confirmText="Reset"
        message={
          <Box sx={{ mt: 2 }}>
            <Typography>
              Generated Password for <strong>{selectedEmployee?.fullName}</strong>:
            </Typography>
            <Box 
              sx={{ 
                mt: 1, 
                p: 2, 
                bgcolor: "#f0f0f0", 
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px dashed #ccc'
              }}
            >
              <Typography variant="h6" sx={{ letterSpacing: 2, fontFamily: 'monospace' }}>
                {tempPassword}
              </Typography>
              <IconButton onClick={() => navigator.clipboard.writeText(tempPassword)}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Copy this password and provide it to the employee.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}

export default EmployeeList;